import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { buildPostRequest } from '../api/apiClient';
import { DecommissionASiteResultStatus } from '../context/DecommissionASiteContext';

interface XrinvRequestData {
  xrinv_in_01: any;
}

interface XritmRequestData {
  xritm_in_01: any;
}

interface XrdlgRequestData {
  xrdlg_in_01: any;
}

export function validateMpxn(mpxn: string): string {
  if (mpxn.length === 0) {
    return 'Error: Please enter the MPxN';
  }

  if (!/^\d{6,13}$/.test(mpxn)) {
    return 'Error: Please check the MPxN';
  }

  return '';
}

export function validateMsn(msn: string): string {
  if (msn.length === 0) {
    return 'Error: Please enter the MSN';
  }

  if (!/^[a-zA-Z0-9]{8,14}$/.test(msn)) {
    return 'Error: Please check the MSN';
  }

  return '';
}

export function validateGuid(guid: string) {
  if (guid.length === 0) {
    return 'Error: Please enter a GUID';
  }

  if (!/^([0-9a-fA-F]{2}-){7}[0-9a-fA-F]{2}$/.test(guid)) {
    return 'The GUID should be 16 characters long';
  }

  return '';
}

export interface NewCommsHubGuidErrors {
  chfError: string;
  gpfError: string;
}

const VALID_OUI_PREFIXES = ['88-73-84', 'E4-FE-D9', '00-0B-6B'];

function validateNewCommsHubGuid(guid: string): string {
  const error = validateGuid(guid);
  if (error) {
    return error;
  }

  if (!VALID_OUI_PREFIXES.includes(guid.slice(0, 8).toUpperCase())) {
    return 'The GUID must start with 88-73-84, E4-FE-D9 or 00-0B-6B';
  }

  return '';
}

export function validateNewCommsHubGuids(newChfId: string, newGpfId: string): NewCommsHubGuidErrors {
  const chfError = validateNewCommsHubGuid(newChfId);
  const gpfError = validateNewCommsHubGuid(newGpfId);

  if (!chfError && !gpfError && newChfId.slice(-8).toUpperCase() !== newGpfId.slice(-8).toUpperCase()) {
    const mismatchError = 'The last 3 octets of the CHF and GPF GUIDs must match';
    return { chfError: mismatchError, gpfError: mismatchError };
  }

  return { chfError, gpfError };
}

// Input mask for comms hub GUID fields: strips non-hex characters, caps at 16 hex
// digits and groups them into dash-separated octets (e.g. "8873 84" -> "88-73-84")
export const formatGuid = (value: string) => {
  const cleanValue = value.replace(/[^0-9a-fA-F]/g, '');
  const limitedValue = cleanValue.slice(0, 16);

  return limitedValue.match(/.{1,2}/g)?.join('-') || limitedValue;
};

export function buildXrinvRequest(mpxn: string): any {
  const xrinvRequestData: XrinvRequestData = {
    xrinv_in_01: {
      Identifier: mpxn,
    },
  };

  return buildPostRequest({ data: [xrinvRequestData] });
}

export function buildXritmRequest(mpxn: string, msn: string): any {
  const xritmRequestData: XritmRequestData = {
    xritm_in_01: {
      ImportMPxN: mpxn,
      MSN: msn,
    },
  };

  return buildPostRequest({ data: [xritmRequestData] });
}

export function buildXrdlgRequest(mpxn: string, chfGuid: string | undefined): any {
  const xrdlgRequestData: XrdlgRequestData = {
    xrdlg_in_01: {
      ImportMPxN: mpxn,
      Identifier: chfGuid,
    },
  };

  return buildPostRequest({ data: [xrdlgRequestData] });
}

export function makeReadableTimeStamp(readingTimeStamp: string | null | undefined) {
  if (!readingTimeStamp) {
    return '';
  }

  const date = new Date(readingTimeStamp);
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };
  return date.toLocaleDateString('en-GB', options).replace(/,/, ' ');
}

export function isMpxnValid(mpxn: string): boolean {
  if (mpxn.length === 0) {
    return false;
  }

  return /^\d{6,13}$/.test(mpxn);
}

export function getMpxnError(mpxn: string): string {
  if (mpxn.length === 0) {
    return 'Please enter the MPxN';
  }

  if (!/^\d{6,13}$/.test(mpxn)) {
    return 'The MPxN is incorrect. It must be between 6-13 digits.';
  }

  return '';
}

export function isMsnValid(msn: string): boolean {
  if (msn.length === 0) {
    return false;
  }

  return /^[a-zA-Z0-9]{8,14}$/.test(msn);
}

export function getMsnError(msn: string): string {
  if (msn.length === 0) {
    return 'Please enter the MSN';
  }

  if (!/^[a-zA-Z0-9]{8,14}$/.test(msn)) {
    return 'Please check the MSN';
  }

  return '';
}

export const getAuthTokenOrLogOut = async () => {
  try {
    const session = await fetchAuthSession();
    if (!session.tokens) {
      console.error('Token has expired or an error occurred:');
      return { userEmail: null, token: null };
    }
    const token = session.tokens?.accessToken.toString() ?? null;
    const userEmail = session.tokens?.signInDetails?.loginId;
    return { userEmail, token };
  } catch (error) {
    console.error('Token has expired or an error occurred:', error);

    await signOut();
    return { userEmail: null, token: null };
  }
};

export interface DecommissionReportingRequest {
  decommissionJourneyId: string;
  contactReason: string;
  mpxn: string;
  msn: string;
  assetType: AssetType;
  engineerEmail: string;
  engineerOnsite: EngineerOnSite;
  decommissionReason: string;
  decommissionResultStatus: string;
  decommissionStartingTimeStamp: string;
  decommissionResultTimeStamp: string;
  decommissionTimeTaken: number;
}

export interface CommsCheckReportingRequest {
  decommissionJourneyId?: string;
  contactReason: string;
  mpxn: string;
  msn: string;
  commsCheckResultStatus: string;
  commsCheckStartingTimeStamp: string;
  commsCheckResultTimeStamp: string;
  commsCheckTimeTaken: number;
  engineerEmail: string;
}

export interface InventoryCheckReportingRequest {
  decommissionJourneyId: string;
  contactReason: string;
  mpxn: string;
  engineerEmail: string;
  inventoryCheckResultStatus: string | null;
  inventoryCheckStartingTimeStamp: string;
  inventoryCheckResultTimeStamp: string;
  inventoryCheckTimeTaken: number;
}

export interface SiteDecommissionReportingRequest {
  decommissionJourneyId: string;
  chfId: string;
  engineerOnsite: EngineerOnSite;
  decommissionResultStatus: DecommissionASiteResultStatus | null;
  decommissionStartingTimeStamp: string;
  decommissionResultTimeStamp: string;
  decommissionTimeTaken: number;
}

export interface TcsoReportingRequest {
  journeyId: string;
  tcsoStartingTimeStamp: string;
  tcsoResultTimeStamp: string;
  tcsoResultStatus: string | null;
  newChfId: string;
  newGpfId: string;
  commsHubLocation: string;
  aerialInstalled: string;
  tcsoTimeTaken: number;
}

export enum AssetType {
  ELEC = 'ELEC',
  GAS = 'GAS',
  NOT_DEFINED = 'NOT_DEFINED',
}

export enum EngineerOnSite {
  YES = 'YES',
  NO = 'NO',
  NOT_DEFINED = 'NOT_DEFINED',
}

export const reportingUtils = {
  mapMeterTypeRadioAnswerAssetType(radioAnswer: string): AssetType {
    switch (radioAnswer) {
      case 'Electric':
        return AssetType.ELEC;
      case 'Gas':
        return AssetType.GAS;
      default:
        return AssetType.NOT_DEFINED;
    }
  },

  mapOnsiteRadioAnswerToEngineerOnSite(radioAnswer: string): EngineerOnSite {
    switch (radioAnswer) {
      case 'Yes':
        return EngineerOnSite.YES;
      case 'No':
        return EngineerOnSite.NO;
      default:
        return EngineerOnSite.NOT_DEFINED;
    }
  },

  mapResultStateToSiteDecommissionResultStatus(status: string): DecommissionASiteResultStatus {
    switch (status) {
      case 'SUCCESS':
        return DecommissionASiteResultStatus.SUCCESS;
      case 'FAIL':
        return DecommissionASiteResultStatus.FAIL;
      case 'PROCESSING':
        return DecommissionASiteResultStatus.PROCESSING;
      case 'TIMEOUT':
        return DecommissionASiteResultStatus.TIMEOUT;
      default:
        return DecommissionASiteResultStatus.NOT_DEFINED;
    }
  },
};

export function getParamsFromURL() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return Object.fromEntries(urlSearchParams.entries());
}

export function parseSmetsVersion(deviceGbcsVersionsVersion: string) {
  const isDeviceGbcsVersionsValid = RegExp(/^\d+(\.\d+)?$/).test(deviceGbcsVersionsVersion);
  if (!isDeviceGbcsVersionsValid) {
    return 'Unknown';
  }

  if (parseInt(deviceGbcsVersionsVersion) == 0) {
    return 'Smets 1';
  } else {
    return 'Smets 2';
  }
}

export function getAddress(l2Response: any): string {
  return (
    l2Response.atomL2response['0']?.xml_string?.Body?.XRINV_OUT_02?.InventoryDevice[
      '0'
    ]?.PropertyFilter?.AddressIdentifier?.replace(/,+/g, ' ') ?? ''
  );
}
